import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { archiveProductMutationOptions } from '@/features/products/api/archive-product.api';
import { GET_LIST_PRODUCT_QUERY_KEY } from '@/features/products/api/get-list-product.api';

export interface ArchiveProductDialogProps extends BaseDialogProps {
  productId: string;
  productTitle: string;
}

export default function ArchiveProductDialog({
  dialog,
  productId,
  productTitle,
}: ArchiveProductDialogProps) {
  const { closeArchiveProductDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const archiveProductMutation = useMutation({
    ...archiveProductMutationOptions(),
    onSuccess: () => {
      toast.success('Produk berhasil diarsipkan');
      queryClient.invalidateQueries({ queryKey: [GET_LIST_PRODUCT_QUERY_KEY] });
      closeArchiveProductDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal mengarsipkan produk';
      toast.error(message);
    },
  });

  const isPending = archiveProductMutation.isPending;

  return (
    <AlertDialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState && !isPending) {
          closeArchiveProductDialog();
        }
      }}
    >
      <AlertDialogContent showCloseButton={false}>
        <AlertDialogHeader>
          <AlertDialogTitle>Arsipkan produk ini?</AlertDialogTitle>
          <AlertDialogDescription>
            Produk "{productTitle}" akan diarsipkan dan tidak dapat dilihat
            publik.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Batal</AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={() =>
              archiveProductMutation.mutate({ product_id: productId })
            }
          >
            {isPending ? 'Memproses...' : 'Arsipkan'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
